import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { supabase } from "../lib/supabaseClient";
import { AppHeader } from "../components/AppHeader";
import { User, Mail, Building2, MapPin, Loader2, Users, ArrowRight } from "lucide-react";
import { DemoVideo } from "../components/DemoVideo";
import { toast } from "sonner";
import { PersistentLayout } from "../components/EditableLayoutRow";
import { LaunchStatusBanner } from "../components/LaunchStatusBanner";

export default function Dashboard() {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    const load = async () => {
      const { data, error } = await supabase
        .from("gp_users")
        .select("*")
        .eq("auth_user_id", user.id)
        .maybeSingle();
      if (!active) return;
      if (error) toast.error("Could not load profile: " + error.message);
      setProfile(data);
      setLoading(false);
    };
    load();
    return () => { active = false; };
  }, [user]);

  const firstName = profile?.first_name || user?.user_metadata?.first_name || "there";

  return (
    <div className="min-h-screen bg-background">
      <AppHeader firstName={firstName} />
      <LaunchStatusBanner audience="gp" />

      <main className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8 animate-fadein" data-testid="dashboard-page">
        <h1 className="font-heading text-3xl font-semibold tracking-tight text-slate-900">
          Welcome back, <span data-testid="dashboard-welcome-name">{firstName}</span>
        </h1>
        <div className="mt-1 flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-slate-500">Here's an overview of your clinic profile.</p>
          <DemoVideo src="/videos/gp.mp4" title="How GPs use SimplyRefer" buttonLabel="See how it works" size="sm" testId="gp-demo-video" />
        </div>

        {loading ? (
          <div className="mt-10 flex items-center gap-2 text-slate-500" data-testid="dashboard-loading">
            <Loader2 className="h-5 w-5 animate-spin" /> Loading your profile…
          </div>
        ) : (
          <PersistentLayout
            scope="gp-dashboard-sections"
            direction="vertical"
            className="mt-8 flex flex-col gap-6"
            items={[
              { id: "account-overview", label: "Account overview", node: (
                <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
                  <InfoCard icon={User} label="First Name" value={profile?.first_name || user?.user_metadata?.first_name || "—"} testId="dashboard-first-name" />
                  <InfoCard icon={Mail} label="Email" value={profile?.email || user?.email || "—"} testId="dashboard-email" />
                  <InfoCard icon={Building2} label="Clinic Name" value={profile?.clinic_name || user?.user_metadata?.clinic_name || "—"} testId="dashboard-clinic-name" />
                </div>
              ) },
              { id: "profile-information", label: "Profile information", node: profile ? (
                <div className="rounded-md border border-slate-200 bg-white p-6">
                  <h2 className="font-heading text-lg font-semibold text-slate-900">Profile Information</h2>
                  <dl className="mt-4 grid grid-cols-1 gap-x-8 gap-y-4 sm:grid-cols-2">
                    <Detail label="Full name" value={((profile.first_name || "") + " " + (profile.last_name || "")).trim() || "—"} testId="profile-full-name" />
                    <Detail label="Email" value={profile.email || "—"} testId="profile-email" />
                    <Detail label="Clinic" value={profile.clinic_name || "—"} testId="profile-clinic" />
                    <Detail label="Suburb" value={profile.suburb || "—"} testId="profile-suburb" />
                  </dl>
                </div>
              ) : null },
              { id: "find-a-clinician", label: "Find a clinician", node: (
                <Link
                  to="/clinicians"
                  data-testid="dashboard-find-clinicians-cta"
                  className="group flex items-center justify-between rounded-md border border-slate-200 bg-white p-6 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md"
                >
                  <div className="flex items-center gap-4">
                    <div className="flex h-11 w-11 items-center justify-center rounded-md bg-secondary text-secondary-foreground"><Users className="h-5 w-5" /></div>
                    <div><p className="font-heading text-base font-semibold text-slate-900">Find a clinician</p><p className="text-sm text-slate-500">Search by specialty, suburb and availability, then complete one of the three referral options.</p></div>
                  </div>
                  <ArrowRight className="h-5 w-5 text-primary transition-transform duration-200 group-hover:translate-x-1" />
                </Link>
              ) },
            ]}
          />
        )}
      </main>
    </div>
  );
}

const InfoCard = ({ icon: Icon, label, value, testId }) => (
  <div className="rounded-md border border-slate-200 bg-white p-6 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md">
    <div className="flex items-center gap-2 text-slate-500">
      <Icon className="h-4 w-4" />
      <span className="text-xs font-medium uppercase tracking-wide">{label}</span>
    </div>
    <p className="mt-2 break-words font-heading text-xl font-semibold text-slate-900" data-testid={testId}>
      {value}
    </p>
  </div>
);

const Detail = ({ label, value, testId }) => (
  <div>
    <dt className="text-xs font-medium uppercase tracking-wide text-slate-400">{label}</dt>
    <dd className="mt-1 text-base text-slate-800" data-testid={testId}>{value}</dd>
  </div>
);
